import { observer } from "mobx-react-lite";

import { Heading } from "@bps/fluent-ui";
import {
  OPTOUT,
  OutboundCommChannel,
  OutboundCommType
} from "@libs/gateways/comms/CommsGateway.dtos.ts";
import { CommunicationType } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { nameOfFactory } from "@libs/utils/name-of.utils.ts";
import { useBookingCalendarScreenContext } from "@modules/booking/screens/booking-calendar/context/BookingCalendarScreenContext.tsx";
import { CalendarEventReminderSearchModel } from "@stores/booking/models/CalendarEventReminderSearchModel.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { CheckboxField } from "@ui-components/form/CheckboxField.tsx";
import { OptionsSelectField } from "@ui-components/form/selects/OptionsSelectField.tsx";
import { SubmissionFormDialog } from "@ui-components/form/submission-form-dialog/SubmissionFormDialog.tsx";

interface UpdateConsentModalProps {
  item: CalendarEventReminderSearchModel;
  onDismiss: () => void;
  onSaved: () => void;
}

interface UpdateConsentFormValues {
  consent: string;
  resendReminder: boolean;
}

const nameOf = nameOfFactory<UpdateConsentFormValues>();

const consentOptions = [
  { key: OutboundCommChannel.Sms, text: "SMS" },
  { key: OPTOUT, text: "OPTOUT" }
];

export const UpdateConsentModal: React.FC<UpdateConsentModalProps> = observer(
  ({ item, onDismiss, onSaved }) => {
    const { comms, notification } = useStores();
    const { setReminderArgs } = useBookingCalendarScreenContext();

    const contactPreferences = item.patient?.contactPreferences;
    const existingPrefs = contactPreferences?.commTypePreferences ?? [];
    const existingApptRem = existingPrefs.find(
      p => p.commTypeCode === OutboundCommType.ApptReminder
    );

    let initialConsentValue: string = "";
    if (existingApptRem) {
      if (existingApptRem.contactHasOptedOut) {
        initialConsentValue = OPTOUT;
      } else {
        initialConsentValue = OutboundCommChannel.Sms;
      }
    }

    const onSubmit = async (values: UpdateConsentFormValues) => {
      const mobileCommunication = item.patient?.communications?.find(
        c => c.type === CommunicationType.Mobile
      );

      const commTypePreferences = [
        ...existingPrefs.filter(p => p !== existingApptRem),
        values.consent === OPTOUT
          ? {
              commTypeCode: OutboundCommType.ApptReminder,
              contactHasOptedOut: true
            }
          : {
              ...existingApptRem,
              commTypeCode: OutboundCommType.ApptReminder,
              preferredCommChannelTypeCode: OutboundCommChannel.Sms,
              preferredCommAddressValue: mobileCommunication?.value,
              contactHasOptedOut: false
            }
      ];

      if (contactPreferences) {
        await comms.patchContactPreferences({
          id: contactPreferences.id,
          eTag: contactPreferences.eTag,
          commTypePreferences
        });
      } else {
        await comms.addContactPreferences({
          id: item.patientId,
          commTypePreferences
        });
      }

      if (values.resendReminder) {
        setReminderArgs({
          calendarEventId: item.calendarEvent.id,
          contactId: item.patientId
        });
      }
    };

    const onSubmitSucceeded = () => {
      onSaved();
      onDismiss();
    };

    return (
      <SubmissionFormDialog<UpdateConsentFormValues>
        dialogName={`Update SMS consent for ${item.patientName}`}
        onSubmit={onSubmit}
        onSubmitSucceeded={onSubmitSucceeded}
        onSubmitFailed={() =>
          notification.error("Failed to update consent. Please try again.")
        }
        initialValues={{ consent: initialConsentValue, resendReminder: false }}
        buttonsProps={{
          submitButtonProps: { text: "Save" },
          cancelButtonProps: { text: "Cancel" },
          hideButtonsSeparator: true
        }}
        dialogProps={{
          minWidth: 500,
          onDismiss,
          dialogContentProps: {
            title: `Update SMS consent for ${item.patientName}`
          }
        }}
      >
        {renderProps => (
          <>
            <OptionsSelectField
              name={nameOf("consent")}
              label="SMS consent"
              placeholder="Select consent"
              options={consentOptions}
              styles={{ root: { width: 220 } }}
              hideClearButton
              hideSearchOption
            />

            <Heading labelPaddings>Appointment reminder</Heading>
            <CheckboxField
              name={nameOf("resendReminder")}
              label="Resend reminder SMS"
              disabled={renderProps.values.consent !== OutboundCommChannel.Sms}
            />
          </>
        )}
      </SubmissionFormDialog>
    );
  }
);
